import { confirmarCodigo, pedirCodigo } from '@sheafyield/sync';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { ProvedorDeToasts } from '../ds/Toast.js';
import { ProvedorDeExclusao } from '../pessoas/FolhaExcluir.js';
import { donoDaSessao, CONTA_LOCAL } from '../dados/dono.js';
import { useDiaLocal } from '../hoje/relogio.js';
import { BarraDeAbas, type Aba } from '../ds/BarraDeAbas.js';
import { Salvaguarda } from '../ds/Salvaguarda.js';
import { ICONES } from '../ds/icones.js';
import { AtualizacaoDisponivel } from '../pwa/AtualizacaoDisponivel.js';
import { PortaoDaTrava } from '../seguranca/TelaTrancada.js';
import { abrirComChaveDoAparelho } from '../seguranca/chaveDoAparelho.js';
import { contaDoAparelho } from '../seguranca/donoDoAparelho.js';
import { observarSegundoPlano, trava } from '../seguranca/trava.js';
import { banco, cliente, iniciarApp } from './contexto.js';
import './casca.css';

const dono = donoDaSessao(cliente);
function assinarConta(aoMudar: (user_id: string) => void) {
  const { data } = cliente.auth.onAuthStateChange((_evento, sessao) => {
    aoMudar(sessao?.user.id ?? CONTA_LOCAL);
  });
  return () => data.subscription.unsubscribe();
}

const ABAS: ReadonlyArray<Aba> = [
  { para: '/hoje', rotulo: 'Hoje', icone: ICONES.hoje },
  { para: '/pessoas', rotulo: 'Pessoas', icone: ICONES.pessoas },
  { para: '/territorio', rotulo: 'Território', icone: ICONES.territorio },
  { para: '/relatorio', rotulo: 'Relatório', icone: ICONES.relatorio },
  { para: '/ajustes', rotulo: 'Ajustes', icone: ICONES.ajustes },
];

/** O nome da aba em que se está, inclusive nas telas de detalhe
 *  (`/pessoas/42` continua sendo Pessoas). Fora das abas — a página não
 *  encontrada — o cabeçalho fica com o nome do app, que é o que sobra de
 *  verdadeiro. */
function tituloDaRota(caminho: string): string {
  const aba = ABAS.find(({ para }) => {
    const raiz = String(para);
    return caminho === raiz || caminho.startsWith(`${raiz}/`);
  });
  return aba?.rotulo ?? 'Sheafyield';
}

function DataDeHoje() {
  const dia = useDiaLocal();
  return (
    <p className="cabecalho__data">
      <time dateTime={dia}>
        <span className="cabecalho__data-completa">
          {new Intl.DateTimeFormat('pt-BR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          }).format(new Date(dia))}
        </span>
        <span className="cabecalho__data-curta">
          {new Intl.DateTimeFormat('pt-BR', {
            day: 'numeric',
            month: 'long',
          }).format(new Date(dia))}
        </span>
      </time>
    </p>
  );
}

export function Casca() {
  const caminho = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    void iniciarApp();
  }, []);
  // Quem conta o tempo em segundo plano é a página, e a casca é a única que
  // vive tanto quanto ela. O motor de sincronização continua rodando com o
  // app trancado, de propósito: destrancar entrega dados atuais, e o que a
  // trava esconde é a tela, não a rede.
  useEffect(() => observarSegundoPlano(), []);
  // A preferência da trava mora na tabela `estado` do Dexie, junto da sessão
  // — e não no `localStorage`, que o Safari descarta sob pressão de espaço
  // enquanto o IndexedDB sobrevive: ali a evicção **abria** o app sozinha.
  // Ler é assíncrono, e até a resposta chegar o portão fica fechado.
  useEffect(() => {
    void trava.carregar(banco.estado);
  }, []);
  return (
    // O portão fica por fora de tudo — cabeçalho, conteúdo e barra de abas.
    // Trancado, nada disso chega a montar: o nome da aba já diria em que
    // parte do app se estava, e a tela de detalhe de uma pessoa carrega o
    // nome dela.
    // `abrirComChaveDoAparelho`, e **não** `entrarComPasskey`: aquele é o
    // login e vai ao GoTrue, de modo que destrancar exigia rede justamente
    // onde a trava fecha — de volta do Mapas, seis minutos depois, no meio
    // do campo e sem sinal. Este é `navigator.credentials.get` local. A
    // entrada por código é a segunda saída, para o dia em que a chave some —
    // e `contaDoAparelho` é o que impede essa saída de virar troca de conta:
    // com sessão aberta, só o e-mail dela entra por ali — e **sem** sessão
    // vale o dono lembrado no Dexie, senão sair da conta reabriria a porta
    // sobre o banco do dono anterior.
    <PortaoDaTrava
      desbloquear={() => abrirComChaveDoAparelho()}
      entradaPorCodigo={{
        pedirCodigo: (email) => pedirCodigo(cliente, email),
        confirmarCodigo: (email, codigo) =>
          confirmarCodigo(cliente, email, codigo),
        contaDoAparelho: () => contaDoAparelho(cliente, banco.estado),
      }}
    >
      <div className="tela">
        {/* Preso ao topo, e dono da área segura: sem ele o conteúdo rolava por
          baixo do relógio e da bateria do iPhone. */}
        <header
          className={
            caminho === '/hoje' ? 'cabecalho cabecalho--hoje' : 'cabecalho'
          }
        >
          {caminho === '/hoje' ? <DataDeHoje /> : null}
          {/* O `h1` da tela mora aqui, e só aqui. Enquanto ele era um parágrafo
            e cada tela trazia o seu, o usuário lia "Ajustes" duas vezes,
            uma coladinha na outra — e em AX5 isso é uma tela inteira de
            cabeçalho antes de qualquer conteúdo. Como o cabeçalho é preso, o
            título de nível 1 fica sempre à mão do VoiceOver, que é o que a
            hierarquia de cabeçalhos existe para dar. */}
          <h1 className="cabecalho__titulo">{tituloDaRota(caminho)}</h1>
        </header>
        <main className="conteudo">
          {/* A fronteira de erro da casca. Sem ela, um `throw` em qualquer tela
            dava tela branca — no aparelho, offline, no meio do campo, que é
            justamente onde o app tem de aguentar (spec §1, robustez). A
            `key` é o caminho: navegar para outra aba remonta a fronteira, e
            sem isso a tela quebrada continuaria quebrada depois de sair
            dela. O cabeçalho e as abas ficam de fora de propósito — são o
            que sobra de navegável quando o conteúdo falha. */}
          <ProvedorDeToasts>
            <ProvedorDeExclusao
              banco={banco}
              dono={dono}
              assinarConta={assinarConta}
            >
              <Salvaguarda
                key={caminho}
                titulo={`Não foi possível mostrar ${tituloDaRota(caminho)}`}
              >
                <Outlet />
              </Salvaguarda>
            </ProvedorDeExclusao>
          </ProvedorDeToasts>
        </main>
        <BarraDeAbas abas={ABAS} />
        <AtualizacaoDisponivel />
      </div>
    </PortaoDaTrava>
  );
}
